import React, { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { spotlightAtom, closeSpotlight } from '@/states/spotlight';
import { SingleStepSpotlight } from './single-step-spotlight';

/**
 * SpotlightManager is a global component that listens to spotlight state
 * and renders the spotlight when triggered from anywhere in the app.
 *
 * Add this component once at the root level of your app.
 */
export const SpotlightManager: React.FC = () => {
  const [spotlight, setSpotlight] = useAtom(spotlightAtom);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!spotlight.isActive || !spotlight.target) {
      setIsReady(false);
      return;
    }

    // Helper to expand collapsible parent sections
    const expandCollapsibleParent = (target: string) => {
      const element = document.querySelector(`[data-tour="${target}"]`);
      if (!element) return false;

      // Find the collapsible trigger button (parent section)
      const collapsibleTrigger = element.closest('[data-state]');
      if (collapsibleTrigger) {
        const currentState = collapsibleTrigger.getAttribute('data-state');
        if (currentState === 'closed') {
          // Find and click the trigger button to expand
          const trigger = collapsibleTrigger.querySelector('button');
          if (trigger) {
            trigger.click();
            return true; // Needs time to expand
          }
        }
      }
      return false; // Already expanded or no collapsible
    };

    // Wait for element to be visible and expand if needed
    const waitForElement = async () => {
      const needsExpansion = spotlight.target
        ? expandCollapsibleParent(spotlight.target)
        : false;

      // Wait a bit longer if we expanded a section
      const delay = needsExpansion ? 500 : 100;

      await new Promise(resolve => setTimeout(resolve, delay));

      // Verify element exists
      const element = document.querySelector(
        `[data-tour="${spotlight.target}"]`
      );

      if (element) {
        setIsReady(true);
      } else if (spotlight.target === 'sidebar-reports-item') {
        // Fallback: try the parent if sub-item doesn't exist
        // If Reports sub-item isn't visible, highlight the parent Reporting section
        setSpotlight({
          ...spotlight,
          target: 'sidebar-reporting',
        });
      }
    };

    waitForElement();
  }, [spotlight.isActive, spotlight.target, setSpotlight]);

  const handleClose = () => {
    setSpotlight(closeSpotlight());
    setIsReady(false);
  };

  if (!spotlight.isActive || !spotlight.target || !isReady) {
    return null;
  }

  return (
    <SingleStepSpotlight
      target={`[data-tour="${spotlight.target}"]`}
      content={spotlight.content}
      run={spotlight.isActive}
      onClose={handleClose}
      placement="right"
      offset={10}
    />
  );
};
